import { useQuery } from '@tanstack/react-query'
import { CopyIcon, LogInIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { getUserClient, UserComponents } from '@/api'
import { Icons } from '@/components/ui/icons'
import { toast } from '@/components/ui/use-toast'

type UserV2 = UserComponents.Schemas.UserV2

import { LoginAsUserDialog } from './login-as-user-dialog'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'

interface TestUserPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  orgName?: string
  copyToken?: boolean
}

export function TestUserPickerDialog({
  open,
  onOpenChange,
  orgId,
  orgName,
  copyToken,
}: TestUserPickerDialogProps) {
  const [loginAsDialogOpen, setLoginAsDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<{
    email: string
    orgId: string
  }>()

  const { data, isLoading } = useQuery({
    queryKey: ['test-users', orgId],
    queryFn: async () => {
      const res = await getUserClient().getUsersV2({
        org_id: orgId,
        search: 'testuser.epilot.cloud',
        limit: 50,
      })

      return (res.data.results ?? []) as UserV2[]
    },
    enabled: open,
  })

  useEffect(() => {
    if (isLoading || !data || !open) return

    const activeUsers = data.filter((u) => u.status === 'Active')

    if (data.length === 0) {
      onOpenChange(false)
      toast({
        title: 'No test users found',
        description: `No test users exist for ${orgName ? orgName : `Org ${orgId}`}`,
      })
    } else if (activeUsers.length === 1) {
      setSelectedUser({ email: activeUsers[0].email!, orgId })
      onOpenChange(false)
      setLoginAsDialogOpen(true)
    }
  }, [data, isLoading, open, orgId, orgName, onOpenChange])

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="pr-6">
            <DialogTitle className="truncate text-foreground">
              {copyToken ? 'Copy Test User Token' : 'Login as Test User'}
            </DialogTitle>
            <DialogDescription>
              {orgName ? `${orgName} — ` : ''}Org {orgId}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icons.spinner className="w-6 h-6 animate-spin" />
            </div>
          ) : !data?.length ? (
            <div className="py-8 text-center text-muted-foreground">
              No test users found
            </div>
          ) : (
            <div className="grid gap-2 max-h-80 overflow-y-auto">
              {data.map((user) => (
                <div
                  className="flex items-center justify-between rounded-md border p-3"
                  key={user.id}
                >
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-sm font-medium text-foreground truncate">
                      {user.display_name || user.email}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </span>
                    <Badge
                      className="w-fit"
                      variant={
                        user.status === 'Active' ? 'default' : 'secondary'
                      }
                    >
                      {user.status}
                    </Badge>
                  </div>
                  <Button
                    disabled={user.status !== 'Active'}
                    onClick={() => {
                      setSelectedUser({
                        email: user.email!,
                        orgId,
                      })
                      onOpenChange(false)
                      setLoginAsDialogOpen(true)
                    }}
                    size="sm"
                    variant="outline"
                  >
                    {copyToken ? (
                      <CopyIcon className="w-4 h-4 mr-1" />
                    ) : (
                      <LogInIcon className="w-4 h-4 mr-1" />
                    )}
                    {copyToken ? 'Copy token' : 'Login as'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedUser && (
        <LoginAsUserDialog
          copyToken={copyToken}
          onOpenChange={setLoginAsDialogOpen}
          open={loginAsDialogOpen}
          user={selectedUser}
        />
      )}
    </>
  )
}
